import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  Slide,
  Divider,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Email,
  Phone,
  Login,
  Menu as MenuIcon,
  ArrowDropDown,
  ArrowRight,
  Close,
  PrivacyTip
} from '@mui/icons-material';
import FarmersBankLogo from '../images/Farmersbank_logo.jpg';
import DiamondCardLogo from '../images/diamondcard_logo.png';
import FastCashLogo from '../images/logos.jpg';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const [fastCashDialogOpen, setFastCashDialogOpen] = useState(false);

  const open = Boolean(anchorEl);
  const servicesOpen = Boolean(servicesAnchorEl);

  // Scroll effect for navbar shrink
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Menu Handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileSubMenu(null);
  };
  const handleServicesMenuOpen = (event) => setServicesAnchorEl(event.currentTarget);
  const handleServicesMenuClose = () => setServicesAnchorEl(null);
  const handleMobileSubMenuOpen = (menu) => setMobileSubMenu(menu);
  
  // Fast Cash Dialog Handlers
  const handleFastCashClick = () => {
    setFastCashDialogOpen(true);
  };
  const handleFastCashDialogClose = () => {
    setFastCashDialogOpen(false);
  };

  const mainNavItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    {
      name: 'Services',
      path: '#',
      dropdown: [
        { name: 'Personal Banking', path: '/personal' },
        { name: 'Business Banking', path: '/business' },
        { name: 'Loans & Credit', path: '/loans' },
        { name: 'Investment Solutions', path: '/investments' }
      ]
    },
    { name: 'Careers', path: '/careers', badge: true },
    { name: 'Help Centre', path: '/help-centre' },
    { name: 'Privacy Policy', path: '/privacy-policy', icon: true }
  ];

  return (
    <>
      {/* Top Contact Bar */}
      <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Box sx={{
          backgroundColor: '#3faf29',
          color: 'white',
          py: 0.5,
          px: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          fontFamily: 'Poppins, sans-serif'
        }}>
          <Container maxWidth="xl">
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              {/* Contact Info */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexWrap: 'wrap', 
                gap: 1,
                '& .MuiButton-root': {
                  minHeight: '28px'
                }
              }}>
                <Button
                  color="inherit"
                  href="mailto:info@farmersbank.co.sz"
                  startIcon={<Email fontSize="small" />}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    p: 0.5,
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto'
                  }}
                >
                  info@farmersbank.co.sz
                </Button>
                <Button
                  color="inherit"
                  href="tel:+26825050888"
                  startIcon={<Phone fontSize="small" />}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    p: 0.5,
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto'
                  }}
                >
                  +268 2505 0888
                </Button>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                '& .MuiButton-root': {
                  minHeight: '28px'
                }
              }}>
                <Button
                  variant="contained"
                  startIcon={<Box component="img" src={DiamondCardLogo} alt="Diamond Card" sx={{ width: 100, height: 16 }} />}
                  href="https://www.diamondcard.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#3faf29',
                    color: 'white',
                    borderRadius: '50px',
                    px: 1.5,
                    py: 0.3,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: '#4fc031',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    textTransform: 'none'
                  }}
                >
                 
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Box component="img" src={FastCashLogo} alt="Fast Cash" sx={{ width: 16, height: 16, filter: 'brightness(0) invert(0)' }} />}
                  onClick={handleFastCashClick}
                  sx={{
                    backgroundColor: '#3faf29',
                    color: 'white',
                    borderRadius: '50px',
                    px: 1.5,
                    py: 0.3,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: '#4fc031',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    textTransform: 'none'
                  }}
                >
                  Fast Cash
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Login />}
                  href="https://online.farmersbank.co.sz"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: 'white',
                    color: '#3faf29',
                    borderRadius: '50px',
                    px: 1.5,
                    py: 0.3,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: '#f8fffb',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    textTransform: 'none'
                  }}
                >
                  Online Banking
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Slide>

      {/* Main Navbar */}
      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 0}
        sx={{
          backdropFilter: 'blur(8px)',
          background: scrolled
            ? 'rgba(63, 175, 41, 0.95)'
            : 'linear-gradient(90deg, rgba(63,175,41,0.9), rgba(50,150,35,0.9))',
          transition: 'all 0.3s ease',
          fontFamily: 'Poppins, sans-serif',
          py: scrolled ? 0.3 : 1
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ 
            py: 0, 
            minHeight: '60px !important',
            transition: 'all 0.3s ease'
          }}>
            {/* Logo */}
            <Box
              component="a"
              href="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 2, md: 4 }
              }}
            >
              <Box
                component="img"
                src={FarmersBankLogo}
                alt="Farmers Bank Logo"
                sx={{
                  height: scrolled ? 45 : 50,
                  mr: 1,
                  transition: 'all 0.3s ease',
                  filter: scrolled ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
              />
              {!isMobile && (
                <Typography variant="h6" sx={{ 
                  ml: 1, 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  letterSpacing: '0.5px'
                }}>
                  
                </Typography>
              )}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                {mainNavItems.map((item) => (
                  <React.Fragment key={item.name}>
                    {item.dropdown ? (
                      <Box sx={{ position: 'relative' }}>
                        <Button
                          onClick={handleServicesMenuOpen}
                          endIcon={<ArrowDropDown sx={{ 
                            transition: 'transform 0.2s ease',
                            transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }} />}
                          sx={{
                            px: 2,
                            py: 1.5,
                            fontWeight: 500,
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            position: 'relative',
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.1)'
                            },
                            '&:after': {
                              content: '""',
                              position: 'absolute',
                              width: 0,
                              height: '2px',
                              bottom: '8px',
                              left: '50%',
                              backgroundColor: 'white',
                              transition: 'all 0.3s ease',
                              transform: 'translateX(-50%)'
                            },
                            '&:hover:after': {
                              width: '60%'
                            }
                          }}
                        >
                          {item.name}
                        </Button>
                        <Menu
                          anchorEl={servicesAnchorEl}
                          open={servicesOpen}
                          onClose={handleServicesMenuClose}
                          PaperProps={{
                            sx: {
                              mt: 1.5,
                              minWidth: 240,
                              borderRadius: '12px',
                              background: 'rgba(63, 175, 41, 0.95)',
                              backdropFilter: 'blur(12px)',
                              color: 'white',
                              boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              overflow: 'hidden'
                            }
                          }}
                          MenuListProps={{
                            sx: {
                              py: 0
                            }
                          }}
                        >
                          {item.dropdown.map((subItem) => (
                            <MenuItem
                              key={subItem.name}
                              onClick={handleServicesMenuClose}
                              component="a"
                              href={subItem.path}
                              sx={{
                                py: 1.5,
                                px: 2,
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  paddingLeft: '24px'
                                },
                                '&:last-child': {
                                  borderBottom: 'none'
                                }
                              }}
                            >
                              <ArrowRight sx={{ 
                                fontSize: '1rem', 
                                mr: 1.5,
                                opacity: 0.7
                              }} />
                              {subItem.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    ) : (
                      <Button
                        href={item.path}
                        sx={{
                          px: 2,
                          py: 1.5,
                          fontWeight: 500,
                          color: 'white',
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          position: 'relative',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.1)'
                          },
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 0,
                            height: '2px',
                            bottom: '8px',
                            left: '50%',
                            backgroundColor: 'white',
                            transition: 'all 0.3s ease',
                            transform: 'translateX(-50%)'
                          },
                          '&:hover:after': {
                            width: '60%'
                          }
                        }}
                      >
                        {item.badge ? (
                          <Badge 
                            color="secondary" 
                            variant="dot" 
                            overlap="circular"
                            sx={{ 
                              mr: 1,
                              '& .MuiBadge-badge': {
                                right: 3,
                                top: 8,
                                backgroundColor: '#ffeb3b'
                              }
                            }}
                          >
                            {item.name}
                          </Badge>
                        ) : item.icon ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PrivacyTip sx={{ fontSize: '1rem' }} />
                            {item.name}
                          </Box>
                        ) : item.name}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            )}

            {/* Mobile Menu */}
            {isMobile && (
              <>
                <IconButton 
                  size="large" 
                  edge="end" 
                  color="inherit" 
                  onClick={handleMenuOpen}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {open ? (
                    <Close sx={{ fontSize: '1.8rem' }} />
                  ) : (
                    <MenuIcon sx={{ fontSize: '1.8rem' }} />
                  )}
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      width: '90vw',
                      maxWidth: 350,
                      mt: 1,
                      borderRadius: '12px',
                      background: 'rgba(63, 175, 41, 0.98)',
                      backdropFilter: 'blur(12px)',
                      color: 'white',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      overflow: 'hidden',
                      maxHeight: '80vh'
                    }
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {/* Mobile Menu Header */}
                  <Box sx={{ 
                    px: 2, 
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <Avatar 
                      src={FarmersBankLogo} 
                      alt="Farmers Bank"
                      sx={{ 
                        width: 40, 
                        height: 40,
                        mr: 1.5
                      }} 
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Farmers Bank
                    </Typography>
                  </Box>
                  
                  {/* Main Menu Items */}
                  {mobileSubMenu === null ? (
                    <>
                      {/* Quick Actions */}
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<Login />}
                          href="https://online.farmersbank.co.sz"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            background: 'linear-gradient(90deg, #ffffff, #f0f0f0)',
                            color: '#3faf29',
                            borderRadius: '8px',
                            py: 1,
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            mb: 1,
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          Online Banking
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<Box component="img" src={DiamondCardLogo} alt="Diamond Card" sx={{ width: 100, height: 16}} />}
                          href="https://www.diamondcard.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            borderRadius: '8px',
                            py: 1,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            mb: 1,
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                         
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<Box component="img" src={FastCashLogo} alt="Fast Cash" sx={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />}
                          onClick={handleFastCashClick}
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            borderRadius: '8px',
                            py: 1,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          Fast Cash
                        </Button>
                      </Box>
                      
                      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />

                      {/* Navigation Items */}
                      {mainNavItems.map((item) => (
                        <React.Fragment key={item.name}>
                          {item.dropdown ? (
                            <MenuItem
                              onClick={() => handleMobileSubMenuOpen(item.name)}
                              sx={{
                                py: 1.5,
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>{item.name}</Box>
                              <ArrowRight sx={{ opacity: 0.7 }} />
                            </MenuItem>
                          ) : (
                            <MenuItem
                              onClick={handleMenuClose}
                              component="a"
                              href={item.path}
                              sx={{
                                py: 1.5,
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                              }}
                            >
                              {item.badge ? (
                                <Badge 
                                  color="secondary" 
                                  variant="dot" 
                                  overlap="circular"
                                  sx={{ 
                                    mr: 1.5,
                                    '& .MuiBadge-badge': {
                                      right: 3,
                                      top: 8,
                                      backgroundColor: '#ffeb3b'
                                    }
                                  }}
                                >
                                  {item.name}
                                </Badge>
                              ) : item.icon ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PrivacyTip sx={{ fontSize: '1.2rem' }} />
                                  {item.name}
                                </Box>
                              ) : item.name}
                            </MenuItem>
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    /* Submenu Content */
                    <>
                      <MenuItem 
                        onClick={() => setMobileSubMenu(null)}
                        sx={{
                          py: 1.5,
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          backgroundColor: 'rgba(0,0,0,0.1)'
                        }}
                      >
                        <ArrowRight sx={{ 
                          transform: 'rotate(180deg)',
                          mr: 1,
                          opacity: 0.7
                        }} />
                        Back to Menu
                      </MenuItem>
                      
                      {mainNavItems.find(item => item.name === mobileSubMenu)?.dropdown?.map((subItem) => (
                        <MenuItem
                          key={subItem.name}
                          onClick={handleMenuClose}
                          component="a"
                          href={subItem.path}
                          sx={{
                            py: 1.5,
                            pl: 4,
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.1)'
                            }
                          }}
                        >
                          {subItem.name}
                        </MenuItem>
                      ))}
                    </>
                  )}
                </Menu>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Fast Cash Coming Soon Dialog */}
      <Dialog
        open={fastCashDialogOpen}
        onClose={handleFastCashDialogClose}
        aria-labelledby="fast-cash-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f8fffb 0%, #ffffff 100%)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle 
          id="fast-cash-dialog-title" 
          sx={{ 
            backgroundColor: '#3faf29', 
            color: 'white', 
            textAlign: 'center',
            py: 2,
            fontWeight: 600
          }}
        >
          Coming Soon!
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3, textAlign: 'center' }}>
          <Box
            component="img"
            src={FastCashLogo}
            alt="Fast Cash"
            sx={{ 
              width: 64, 
              height: 64, 
              mb: 2,
              mx: 'auto'
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
            Fast Cash Service
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Our Fast Cash service is currently under development and will be available soon.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            Stay tuned for updates!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            onClick={handleFastCashDialogClose}
            variant="contained"
            sx={{
              backgroundColor: '#3faf29',
              color: 'white',
              borderRadius: '50px',
              px: 4,
              py: 1,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#4fc031'
              }
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;